/**
 * Session guard: decides when to lock the portal and demand re-authentication.
 *
 * Three triggers, each chosen because it maps to a real threat rather than to a proxy:
 *
 * - **Face absent** — the workstation was left with an authenticated session open.
 * - **Identity mismatch** — a face is present but it is not the enrolled employee, i.e.
 *   somebody else is working under these credentials.
 * - **Camera unavailable** — the sensor stopped delivering frames while the console was in the
 *   foreground, so presence cannot be established at all.
 *
 * Orientation is deliberately *not* a trigger. Someone reading a printed document is not
 * facing their screen, and in a loan-review office that is continuous. Locking on orientation
 * would fire dozens of times a day, and a security control that interrupts real work gets
 * switched off within a week.
 *
 * **On "inability to observe".** An earlier version of this file held that inability to observe
 * is never a trigger, and lumped an unplugged camera in with a backgrounded tab. That was wrong,
 * and wrong in the direction that matters: it made the entire control opt-out, because disabling
 * the webcam produced silence and silence was treated as benign. The distinction the guard now
 * draws is *why* it cannot see:
 *
 * - **The camera is still delivering frames** — we can see the workstation, so everything
 *   accumulates normally. Tab visibility is irrelevant here: a hidden tab whose camera still
 *   works is not silence, it is a clear view of an empty chair.
 * - **Frames have stopped and the surface is hidden** — the browser is throttling a background
 *   tab, which is it doing its job. Nothing accumulates; nothing locks.
 * - **Frames have stopped while the surface is visible** — a fault, or somebody disabling the
 *   sensor. It locks, on its own longer threshold so a brief drop recovers.
 *
 * That first case was originally wrong, and wrong in the way that mattered most. Suspension keyed
 * on `observable` alone, so *any* hidden tab cleared every clock — and on Windows the tab goes
 * hidden when you switch window, minimise, or let the display sleep. Since leaving your desk
 * normally does one of those, the absence trigger was suspended at the exact moment absence
 * began: measured over several sessions, `face_lost` episodes of 88s, 163s and 171s were recorded
 * with no lock beside them, and `face_absent` had never once fired while the other two triggers
 * worked. The primary trigger was effectively dead in practice.
 *
 * An unverified *identity* still never locks anything: a failed network call genuinely says
 * nothing about who is in the chair, whereas a dead camera says we cannot police the chair at
 * all.
 */

import type { SessionGuardPolicy } from '../config.js';

export type LockReason =
  | 'face_absent'
  | 'identity_mismatch'
  | 'camera_unavailable'
  /** The console was left in the background, where presence cannot be observed. */
  | 'console_backgrounded';

/** Result of comparing a live frame against the enrolled template. */
export type IdentityVerdict = 'match' | 'mismatch' | 'unverified';

export interface IdentityObservation {
  readonly verdict: IdentityVerdict;
  /** Cosine similarity, or null when no comparison was possible. */
  readonly similarity: number | null;
  /** Monotonic time the comparison was performed, for staleness checks. */
  readonly checkedAtMonotonic: number;
}

export interface GuardInput {
  readonly monotonicTs: number;
  /**
   * Whether the *host surface* is visible — a foreground tab or window.
   *
   * On its own this no longer suspends anything: a hidden tab with a working camera is still a
   * clear view of the workstation. It only decides how to read a *stopped* camera — throttling
   * by a background browser, or a fault worth locking on. See `sensorHealthy`.
   */
  readonly observable: boolean;
  /**
   * Whether the camera is delivering usable frames.
   *
   * Defaults to true so an existing caller that does not measure it keeps its previous
   * behaviour. Callers that can tell should pass it: false while `observable` is true is what
   * distinguishes a disabled webcam from a backgrounded window.
   */
  readonly sensorHealthy?: boolean;
  readonly faceDetected: boolean;
  readonly faceCount: number;
  /** Most recent identity comparison, or null if none has been performed yet. */
  readonly identity: IdentityObservation | null;
  /** Optional account of why the sensor is unhealthy, surfaced in the lock evidence. */
  readonly sensorDetail?: string;
}

export interface GuardDecision {
  /** True once the session is locked and re-authentication is required. */
  readonly locked: boolean;
  readonly reason: LockReason | null;
  /**
   * True when the conditions for a lock were met. In `log_only` mode `locked` stays false
   * while this goes true — that difference is the whole point of the mode.
   */
  readonly wouldLock: boolean;
  readonly evidence: readonly string[];
  readonly faceAbsentForSeconds: number;
  readonly identityMismatchForSeconds: number;
  /** How long the camera has been failing to deliver frames while in the foreground. */
  readonly cameraUnavailableForSeconds: number;
  /** True while the guard is not watching, and therefore not accumulating anything. */
  readonly suspended: boolean;
}

export class SessionGuard {
  private readonly policy: SessionGuardPolicy;

  private faceAbsentSince: number | null = null;
  private mismatchSince: number | null = null;
  private sensorDownSince: number | null = null;
  private surfaceHiddenSince: number | null = null;
  private lockedReason: LockReason | null = null;
  private graceUntil = 0;
  private lastObservableAt: number | null = null;
  private lockCount = 0;
  private wouldLockCount = 0;
  private latestDecision: GuardDecision | null = null;

  constructor(policy: SessionGuardPolicy) {
    this.policy = policy;
  }

  get isLocked(): boolean {
    return this.lockedReason !== null;
  }

  /**
   * The most recent decision, or null before the first observation.
   *
   * Exposed for diagnostics. Reconstructing the guard's state from similarity scores alone was
   * guesswork, and got the wrong answer more than once: "the median read mismatch for 47s and
   * nothing locked" and "the guard never saw those verdicts" look identical from outside.
   */
  get lastDecision(): GuardDecision | null {
    return this.latestDecision;
  }

  get lockReason(): LockReason | null {
    return this.lockedReason;
  }

  /** How many times a lock was actually applied. */
  get appliedLockCount(): number {
    return this.lockCount;
  }

  /**
   * How many times conditions for a lock were met, whether or not one was applied.
   *
   * In `log_only` mode this is the number to look at: it is the false-positive rate you would
   * have shipped.
   */
  get triggeredCount(): number {
    return this.wouldLockCount;
  }

  observe(input: GuardInput): GuardDecision {
    const { monotonicTs: now } = input;

    if (!this.policy.enabled) {
      return this.decision(false, null, false, ['session guard is disabled by policy'], now, false);
    }

    // ---- trigger 0: the console is in the background --------------------------------
    // Accumulated before the suspension branch, and deliberately not cleared by it. A hidden
    // tab is exactly where a browser cannot be relied on to observe anything — the video
    // element may stall, so absence there is undetectable rather than merely unobserved. This
    // trigger closes that blind spot instead of leaving it. Zero disables it.
    if (input.observable) {
      this.surfaceHiddenSince = null;
    } else if (this.surfaceHiddenSince === null) {
      this.surfaceHiddenSince = now;
    }
    const hiddenFor =
      this.surfaceHiddenSince === null ? 0 : Math.max(0, now - this.surfaceHiddenSince);
    const hiddenTriggerArmed = this.policy.surfaceHiddenLockSeconds > 0;

    if (
      hiddenTriggerArmed &&
      this.surfaceHiddenSince !== null &&
      hiddenFor >= this.policy.surfaceHiddenLockSeconds &&
      !this.isLocked &&
      now >= this.graceUntil
    ) {
      this.wouldLockCount += 1;
      const evidence = [
        `the console was in the background for ${hiddenFor.toFixed(0)}s ` +
          `(threshold ${this.policy.surfaceHiddenLockSeconds}s)`,
        'presence cannot be established while the console is not in view',
      ];
      if (this.policy.mode === 'log_only') {
        evidence.push(
          'policy mode is log_only: this would have locked the session, but no action was taken',
        );
        this.surfaceHiddenSince = null;
        return this.decision(false, null, true, evidence, now, false);
      }
      this.lockedReason = 'console_backgrounded';
      this.lockCount += 1;
      this.faceAbsentSince = null;
      this.mismatchSince = null;
      this.sensorDownSince = null;
      this.surfaceHiddenSince = null;
      evidence.push('session locked; re-authentication is required to continue');
      return this.decision(true, 'console_backgrounded', true, evidence, now, false);
    }

    // ---- suspension: we genuinely cannot see the workstation ------------------------
    // Only when frames have stopped *and* the surface is hidden. Frames stopping in a
    // background tab is the browser throttling, not evidence about the employee. Frames
    // stopping while the surface is visible falls through to the camera trigger below.
    const sensorHealthyForSuspension = input.sensorHealthy ?? true;
    if (!sensorHealthyForSuspension && !input.observable) {
      // Clocks are cleared rather than paused. Resuming with a half-elapsed absence timer
      // would let an unobserved gap count toward a lock, which is the failure this guards.
      this.faceAbsentSince = null;
      this.mismatchSince = null;
      // Sensor health is cleared too: a camera that stops because the tab was backgrounded is
      // the browser doing its job, not a fault, and must not accumulate toward a lock.
      this.sensorDownSince = null;
      return this.decision(
        this.isLocked,
        this.lockedReason,
        false,
        [
          'the camera has stopped delivering frames while the console is in the background; ' +
            'this is the browser throttling a hidden tab, so absence, identity and camera ' +
            'clocks are suspended rather than counting toward a lock',
        ],
        now,
        true,
      );
    }

    const resumed = this.lastObservableAt === null || now - this.lastObservableAt > 2;
    this.lastObservableAt = now;

    // Already locked: stay locked until unlock() is called after successful re-auth.
    if (this.isLocked) {
      return this.decision(
        true,
        this.lockedReason,
        false,
        [`session is locked (${this.lockedReason}); re-authentication is required to continue`],
        now,
        false,
      );
    }

    const inGrace = now < this.graceUntil;
    const evidence: string[] = [];

    // ---- trigger 1: camera unavailable while in the foreground -----------------------
    // Undefined means the caller does not measure sensor health, which must not silently
    // become "unhealthy" and lock every such caller out.
    const sensorHealthy = input.sensorHealthy ?? true;
    if (sensorHealthy) {
      this.sensorDownSince = null;
    } else if (this.sensorDownSince === null) {
      this.sensorDownSince = now;
    }
    const sensorDownFor =
      this.sensorDownSince === null ? 0 : Math.max(0, now - this.sensorDownSince);

    // ---- trigger 2: face absent ------------------------------------------------------
    // Only meaningful while the camera works. A dead sensor reports no face every frame, and
    // letting that feed the absence clock would attribute a hardware fault to the employee
    // having walked away — and would race the two triggers to produce whichever fired first.
    if (!sensorHealthy) {
      this.faceAbsentSince = null;
    } else if (input.faceDetected) {
      this.faceAbsentSince = null;
    } else if (this.faceAbsentSince === null) {
      // Start the clock on resume rather than retroactively.
      this.faceAbsentSince = now;
    }
    const absentFor = this.faceAbsentSince === null ? 0 : Math.max(0, now - this.faceAbsentSince);

    // ---- trigger 3: identity mismatch ------------------------------------------------
    const identity = this.freshIdentity(input.identity, now);
    if (identity === null) {
      // Unverified: no comparison, a failed comparison, or a stale one. Never a lock.
      this.mismatchSince = null;
      if (input.identity !== null && input.faceDetected) {
        evidence.push(
          'identity could not be verified for this period; an unverified check is not treated ' +
            'as a mismatch',
        );
      }
    } else if (identity.verdict === 'mismatch') {
      if (this.mismatchSince === null) this.mismatchSince = now;
    } else {
      this.mismatchSince = null;
    }
    const mismatchFor = this.mismatchSince === null ? 0 : Math.max(0, now - this.mismatchSince);

    // ---- evaluate ---------------------------------------------------------------------
    let reason: LockReason | null = null;
    // Checked before the others because when the camera is down they cannot have anything to
    // say — and reporting `face_absent` for a dead webcam would tell the employee they had
    // walked away when they had not moved.
    if (sensorDownFor >= this.policy.cameraUnavailableLockSeconds && this.sensorDownSince !== null) {
      reason = 'camera_unavailable';
      evidence.push(
        `the camera stopped delivering frames for ${sensorDownFor.toFixed(0)}s while the ` +
          `console was in the foreground (threshold ${this.policy.cameraUnavailableLockSeconds}s)`,
      );
      if (input.sensorDetail !== undefined && input.sensorDetail.length > 0) {
        evidence.push(input.sensorDetail);
      }
    } else if (
      mismatchFor >= this.policy.identityMismatchLockSeconds &&
      this.mismatchSince !== null
    ) {
      reason = 'identity_mismatch';
      evidence.push(
        `the face in frame did not match the enrolled employee for ${mismatchFor.toFixed(0)}s ` +
          `(threshold ${this.policy.identityMismatchLockSeconds}s)`,
      );
      if (identity?.similarity !== null && identity?.similarity !== undefined) {
        // The similarity only. The guard deliberately does not name a threshold: the operating
        // point lives on the server (WORK_FOCUS_MIN_MATCH_SIMILARITY) and the server applies it,
        // while `policy.minMatchSimilarity` here is only a shipped fallback. Citing the local
        // value produced audit records reading "below the 0.360 match threshold" for locks the
        // server had actually judged at 0.470 — a false statement in the one record we rely on.
        // The authoritative threshold is logged server-side against every comparison.
        evidence.push(`measured similarity ${identity.similarity.toFixed(3)}`);
      }
    } else if (absentFor >= this.policy.faceAbsentLockSeconds) {
      reason = 'face_absent';
      evidence.push(
        `no face detected at the workstation for ${absentFor.toFixed(0)}s ` +
          `(threshold ${this.policy.faceAbsentLockSeconds}s)`,
      );
      if (input.faceCount > 1) {
        evidence.push(`${input.faceCount} faces were in frame during this period`);
      }
    }

    if (reason === null) {
      if (sensorDownFor > 0) {
        evidence.push(
          `the camera has not delivered a usable frame for ${sensorDownFor.toFixed(0)}s, below ` +
            `the ${this.policy.cameraUnavailableLockSeconds}s lock threshold`,
        );
      }
      if (absentFor > 0) {
        evidence.push(
          `no face for ${absentFor.toFixed(0)}s, below the ` +
            `${this.policy.faceAbsentLockSeconds}s lock threshold`,
        );
      }
      if (resumed) evidence.push('observation resumed; clocks restarted from now');
      if (evidence.length === 0) evidence.push('employee present and identity consistent');
      return this.decision(false, null, false, evidence, now, false);
    }

    // Conditions met.
    this.wouldLockCount += 1;

    if (inGrace) {
      evidence.push(
        `within the ${this.policy.graceSecondsAfterUnlock}s grace period after unlocking; ` +
          'not locking again yet',
      );
      return this.decision(false, null, true, evidence, now, false);
    }

    if (this.policy.mode === 'log_only') {
      evidence.push(
        'policy mode is log_only: this would have locked the session, but no action was taken',
      );
      // Clocks are reset so a single sustained condition logs once rather than every frame.
      this.faceAbsentSince = null;
      this.mismatchSince = null;
      this.sensorDownSince = null;
      return this.decision(false, null, true, evidence, now, false);
    }

    this.lockedReason = reason;
    this.lockCount += 1;
    this.faceAbsentSince = null;
    this.mismatchSince = null;
    this.sensorDownSince = null;
    evidence.push('session locked; re-authentication is required to continue');
    return this.decision(true, reason, true, evidence, now, false);
  }

  /**
   * Clear the lock after a successful re-authentication.
   *
   * Starts the grace period, so a person who has just proven who they are is not locked out
   * again while they settle back in front of the camera.
   */
  unlock(monotonicTs: number): void {
    this.lockedReason = null;
    this.faceAbsentSince = null;
    this.mismatchSince = null;
    this.sensorDownSince = null;
    this.surfaceHiddenSince = null;
    this.graceUntil = monotonicTs + this.policy.graceSecondsAfterUnlock;
  }

  /** Force a lock — e.g. an administrator ending a session remotely. */
  lock(reason: LockReason): void {
    if (this.lockedReason === null) {
      this.lockedReason = reason;
      this.lockCount += 1;
    }
  }

  reset(): void {
    this.faceAbsentSince = null;
    this.mismatchSince = null;
    this.sensorDownSince = null;
    this.surfaceHiddenSince = null;
    this.lockedReason = null;
    this.graceUntil = 0;
    this.lastObservableAt = null;
  }

  /** True when an identity re-check is due. */
  identityCheckDue(now: number, identity: IdentityObservation | null): boolean {
    if (identity === null) return true;
    return now - identity.checkedAtMonotonic >= this.policy.identityCheckIntervalSeconds;
  }

  /**
   * Return the identity observation only if it is usable: an actual verdict, recent enough
   * to still describe who is sitting there.
   */
  private freshIdentity(
    identity: IdentityObservation | null,
    now: number,
  ): IdentityObservation | null {
    if (identity === null) return null;
    if (identity.verdict === 'unverified') return null;
    if (now - identity.checkedAtMonotonic > this.policy.identityResultMaxAgeSeconds) return null;
    return identity;
  }

  private decision(
    locked: boolean,
    reason: LockReason | null,
    wouldLock: boolean,
    evidence: readonly string[],
    now: number,
    suspended: boolean,
  ): GuardDecision {
    this.latestDecision = {
      locked,
      reason,
      wouldLock,
      evidence,
      faceAbsentForSeconds:
        this.faceAbsentSince === null ? 0 : Math.max(0, now - this.faceAbsentSince),
      identityMismatchForSeconds:
        this.mismatchSince === null ? 0 : Math.max(0, now - this.mismatchSince),
      cameraUnavailableForSeconds:
        this.sensorDownSince === null ? 0 : Math.max(0, now - this.sensorDownSince),
      suspended,
    };
    return this.latestDecision;
  }
}

/**
 * Turn a similarity score into a verdict.
 *
 * Kept separate from the guard so the operating point can be tuned, and audited, on its own.
 * `null` similarity means no comparison was possible and must not become a mismatch.
 */
export function verdictFromSimilarity(
  similarity: number | null,
  minMatchSimilarity: number,
): IdentityVerdict {
  if (similarity === null || !Number.isFinite(similarity)) return 'unverified';
  return similarity >= minMatchSimilarity ? 'match' : 'mismatch';
}
